/**
 * Plugin Config Schema Registry
 * Maps plugin control type -> config schema for property pages.
 * Allows dynamic plugin property pages without hardcoding in PluginsTab.
 *
 * Deliverable 1 from Phase 3
 */

import type { PluginConfigSchema } from '../components/tabs/pluginConfigSchemas';

/**
 * Dynamic registry for plugin configuration schemas
 * Allows packages to self-register their plugin config schemas
 * without modifying designer-components-react
 */
export class PluginConfigSchemaRegistry {
  private static instance: PluginConfigSchemaRegistry;

  /** Map of plugin type -> schema */
  private schemas: Map<string, PluginConfigSchema> = new Map();

  /**
   * Get the singleton instance
   */
  static getInstance(): PluginConfigSchemaRegistry {
    if (!PluginConfigSchemaRegistry.instance) {
      PluginConfigSchemaRegistry.instance = new PluginConfigSchemaRegistry();
    }
    return PluginConfigSchemaRegistry.instance;
  }

  /**
   * Register a configuration schema for a plugin type
   * @param type Plugin control type (e.g., 'action-library-plugin')
   * @param schema Configuration field schema
   */
  register(type: string, schema: PluginConfigSchema): void {
    if (!type) {
      throw new Error('PluginConfigSchemaRegistry.register: type is required');
    }
    if (!schema) {
      throw new Error('PluginConfigSchemaRegistry.register: schema is required');
    }
    this.schemas.set(type, schema);
  }

  /**
   * Get the configuration schema for a plugin type
   * @param type Plugin control type
   * @returns Schema or undefined if not registered
   */
  get(type: string): PluginConfigSchema | undefined {
    return this.schemas.get(type);
  }

  /**
   * Check if a schema is registered for a plugin type
   * @param type Plugin control type
   * @returns true if schema is registered
   */
  has(type: string): boolean {
    return this.schemas.has(type);
  }

  /**
   * Get all registered plugin types
   * @returns Array of plugin types
   */
  getAllTypes(): string[] {
    return Array.from(this.schemas.keys());
  }

  /**
   * Unregister a plugin type (for cleanup or testing)
   * @param type Plugin control type
   */
  unregister(type: string): void {
    this.schemas.delete(type);
  }

  /**
   * Clear all registered schemas (for testing or full reset)
   */
  clear(): void {
    this.schemas.clear();
  }

  /**
   * Get count of registered schemas
   */
  count(): number {
    return this.schemas.size;
  }
}

/**
 * Convenience export for singleton access
 */
export const pluginConfigSchemaRegistry = PluginConfigSchemaRegistry.getInstance();
